import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, Check, Plus, RefreshCw, Hash } from "lucide-react";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useTags } from "@/hooks/useTags";
import { isKnowledgeBaseDocumentEntity } from "@/lib/tagEntityTypes";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { tagsService } from "@/services/tagsService";
import type { Tag } from "@shared/schema";

interface SmartTagSuggestionsProps {
  content: string;
  title?: string;
  entityType: string;
  entityId: string;
  currentTags?: Tag[];
  onTagsChange?: (tags: Tag[]) => void;
  className?: string;
}

interface TagSuggestion {
  name: string;
  reason: string;
  confidence: number;
}

export function SmartTagSuggestions({
  content,
  title = "",
  entityType,
  entityId,
  currentTags = [],
  onTagsChange,
  className = "",
}: SmartTagSuggestionsProps) {
  const { toast } = useToast();
  const [suggestions, setSuggestions] = useState<TagSuggestion[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);

  const { data: allTags = [] } = useTags();

  const generateSuggestionsMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/suggest-tags", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: content.substring(0, 2000),
          title,
          existingTags: allTags.map((t) => t.name),
          currentTags: currentTags.map((t) => t.name),
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to generate suggestions");
      }
      return res.json();
    },
    onSuccess: (data) => {
      setSuggestions(data.suggestions || []);
      setHasGenerated(true);
    },
    onError: (error: any) => {
      toast({
        title: "Suggestion failed",
        description: error.message || "Could not generate tag suggestions.",
        variant: "destructive",
      });
    },
  });

  const isKbDocument = isKnowledgeBaseDocumentEntity(entityType);

  const addTagMutation = useMutation({
    mutationFn: async (tagData: { tagId?: string; tagName?: string }) => {
      if (isKbDocument) {
        const item = await knowledgeBaseService.getKnowledgeBaseItem(entityId);
        if (!item) throw new Error("Document not found");
        let tagIds = [...(item.tagIds ?? [])];
        if (tagData.tagId) {
          if (!tagIds.includes(tagData.tagId)) tagIds.push(tagData.tagId);
        } else if (tagData.tagName?.trim()) {
          let tag = await tagsService.getTagByName(tagData.tagName.trim());
          if (!tag) {
            tag = await tagsService.createTag({ name: tagData.tagName.trim() });
          }
          if (!tagIds.includes(tag.id)) tagIds.push(tag.id);
        }
        await knowledgeBaseService.updateKnowledgeBaseItem(entityId, { tagIds });
        return;
      }
      return apiRequest(
        "POST",
        `/api/entities/${entityType}/${entityId}/tags`,
        tagData
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/tags"] });
      if (isKbDocument) {
        const item = await knowledgeBaseService.getKnowledgeBaseItem(entityId);
        const tagIdSet = new Set((item?.tagIds ?? []).map(String));
        const freshTags = allTags.filter((t) => tagIdSet.has(String(t.id)));
        onTagsChange?.(freshTags);
        await queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
      } else {
        await queryClient.refetchQueries({
          queryKey: ["/api/entities", entityType, entityId, "tags"],
          type: "active",
          exact: true,
        });
        const freshTags =
          queryClient.getQueryData<Tag[]>([
            "/api/entities",
            entityType,
            entityId,
            "tags",
          ]) || [];
        onTagsChange?.(freshTags);
      }
    },
  });

  const handleApplySuggestion = async (suggestion: TagSuggestion) => {
    const existingTag = allTags.find(
      (t) => t.name.toLowerCase() === suggestion.name.toLowerCase()
    );

    if (existingTag) {
      const alreadyHas = currentTags.some((t) => t.id === existingTag.id);
      if (alreadyHas) {
        toast({
          title: "Already tagged",
          description: `"${suggestion.name}" is already applied.`,
        });
        return;
      }
      addTagMutation.mutate({ tagId: existingTag.id });
    } else {
      addTagMutation.mutate({ tagName: suggestion.name });
    }

    setSuggestions((prev) => prev.filter((s) => s.name !== suggestion.name));
    
    toast({
      title: "Tag applied",
      description: `Added "${suggestion.name}" tag.`,
    });
  };

  const handleApplyAll = async () => {
    for (const suggestion of suggestions) {
      const alreadyHas = currentTags.some(
        (t) => t.name.toLowerCase() === suggestion.name.toLowerCase()
      );
      if (!alreadyHas) {
        await handleApplySuggestion(suggestion);
      }
    }
  };

  const contentTooShort = (content + title).length < 20;

  if (contentTooShort) {
    return null;
  }

  return (
    <div className={`p-4 bg-muted/30 rounded-lg ${className}`} data-testid="smart-tag-suggestions">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Sparkles className="h-4 w-4 text-primary" />
          AI Tag Suggestions
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => generateSuggestionsMutation.mutate()}
          disabled={generateSuggestionsMutation.isPending}
          data-testid="button-generate-tags"
        >
          {generateSuggestionsMutation.isPending ? (
            <>
              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
              Analyzing...
            </>
          ) : hasGenerated ? (
            <>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-1" />
              Suggest Tags
            </>
          )}
        </Button>
      </div>

      {generateSuggestionsMutation.isPending && (
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-6 w-16 rounded-full" />
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-6 w-14 rounded-full" />
        </div>
      )}

      {!generateSuggestionsMutation.isPending && suggestions.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion, i) => (
              <Badge
                key={i}
                variant="secondary"
                className="cursor-pointer hover-elevate flex items-center gap-1 pr-1"
                onClick={() => handleApplySuggestion(suggestion)}
                title={suggestion.reason}
                data-testid={`badge-suggestion-${i}`}
              >
                <Hash className="h-3 w-3" />
                {suggestion.name}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 ml-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApplySuggestion(suggestion);
                  }}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </Badge>
            ))}
          </div>
          {suggestions.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleApplyAll}
              disabled={addTagMutation.isPending}
              data-testid="button-apply-all-tags"
            >
              <Check className="h-4 w-4 mr-1" />
              Apply All ({suggestions.length})
            </Button>
          )}
        </div>
      )}

      {!generateSuggestionsMutation.isPending && hasGenerated && suggestions.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No additional tag suggestions found. Your current tags seem comprehensive!
        </p>
      )}

      {!hasGenerated && !generateSuggestionsMutation.isPending && (
        <p className="text-sm text-muted-foreground">
          Click "Suggest Tags" to get AI-powered tag recommendations based on your content.
        </p>
      )}
    </div>
  );
}
